import { AddCircle, TickCircle } from 'iconsax-react';
import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { XCircle } from 'react-feather';
import { ProblemDetailType } from '@/lib/utils/api';
import { ToastMsg } from '../ui/toast/toast-message';
import { FileItem } from '../ui/uploader/file-uploader';
import ModalBox from '@/components/shared/ModalBox';
import DrawerBox from '@/components/shared/DrawerBox';
import useMobile from '@/hook/useMobile';
import CreateTicketForm, { CreateTicketFormProps } from './CreateTicketForm';
import { postCreateTicket, sendTicketAttachment } from '@/app/api/support/services';
import { CreateTicketRequest } from '@/models/support';
import { formatErrorMessages } from '@/lib/utils/utils';
import { useTicketListRefresh } from '@/store/support';
import { useIsSheetOpen } from '@/store/profiles';

interface CreateTicketModalProps {
  onTicketCreated?: () => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const CreateTicketModal = ({ onTicketCreated, isOpen, setIsOpen }: CreateTicketModalProps) => {
  const isMobile = useMobile();
  const [fileList, setFileList] = useState<FileItem[]>([]);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const setTicketListRefresh = useTicketListRefresh((state) => state.setTicketListRefresh);
  const { IsSheetOpen, setIsSheetOpen } = useIsSheetOpen();
  const [isUploading, setIsUploading] = useState(false);

  const createTicket = useMutation({
    mutationFn: (data: CreateTicketRequest) => postCreateTicket(data),
    onError: (err: ProblemDetailType) => {
      const errorMessage = err ? formatErrorMessages(err) : 'خطا در بارگزاری فایل';
      toast.error(<ToastMsg message={errorMessage} />, {
        icon: <XCircle className='text-red-1' />,
        progressStyle: { backgroundColor: '#f7525f', height: '2px' },
        style: { padding: '15px', zIndex: '100' },
        closeButton: false,
      });
    },
    onSuccess: () => {
      toast.success(<ToastMsg message='تیکت با موفقیت ثبت شد' />, {
        icon: <TickCircle className='text-green-1' />,
        progressStyle: { backgroundColor: '#089981', height: '2px' },
        style: { padding: '15px' },
        closeButton: false,
      });
      handleFormReset();
      setIsOpen(false);
      setTicketListRefresh(true);
      onTicketCreated?.();
      if (IsSheetOpen) setIsSheetOpen(false);
    },
  });

  const handleUpload: CreateTicketFormProps['uploadFn'] = async (file, onProgress) => {
    try {
      setIsUploading(true);
      const response = await sendTicketAttachment(file, onProgress);
      return response;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFormReset = () => {
    setFileList([]);
    setAttachmentIds([]);
  };

  const handleFormSubmit = (data: CreateTicketRequest) => {
    createTicket.mutate({
      ...data,
      attachmentIds: attachmentIds ?? [],
    });
  };

  const handleFileChange = (newFileList: FileItem[]) => {
    setFileList(newFileList);

    const uploadedIds = newFileList
      .filter((file) => file.status === 'done' && typeof file.response === 'string')
      .map((file) => file.response as string);

    setAttachmentIds(uploadedIds);
  };

  const formContent = (
    <CreateTicketForm
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      isLoading={createTicket.isPending}
      fileList={fileList}
      attachmentIds={attachmentIds}
      onFileChange={handleFileChange}
      uploadFn={handleUpload}
      isUploading={isUploading}
    />
  );

  return isMobile ? (
    <DrawerBox
      contentStyle='w-full'
      headerText='ثبت تیکت جدید'
      headerStyle='body-13-bold leading-[24px]'
      headerIcon={<AddCircle size={22} />}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) handleFormReset();
      }}
      usePortal={true}
    >
      {formContent}
    </DrawerBox>
  ) : (
    <ModalBox
      contentStyle=''
      headerText='ثبت تیکت جدید'
      headerIcon={<AddCircle size={22} />}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) handleFormReset();
      }}
      usePortal={true}
    >
      {formContent}
    </ModalBox>
  );
};

export default CreateTicketModal;
